<script setup lang="ts">
import { type MaybeRef, computed, ref, unref } from 'vue';
import BaseInput from './BaseInput.vue';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    placeholder?: string;
    disabled?: boolean;
    autocomplete?: string;
    error?: boolean;
    errorMessage?: MaybeRef<string>;
    name?: string;
    initiallyVisible?: boolean;
  }>(),
  {
    placeholder: 'Password',
    disabled: false,
    autocomplete: 'current-password',
    error: false,
    errorMessage: '',
    name: '',
    initiallyVisible: false,
  },
);

// eslint-disable-next-line func-call-spacing
const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'blur'): void;
  (e: 'focus'): void;
}>();

const isVisible = ref(props.initiallyVisible);
const inputType = computed(() => (isVisible.value ? 'text' : 'password'));
const toggleLabel = computed(() => (isVisible.value ? 'Hide password' : 'Show password'));

const toggleVisibility = () => {
  if (props.disabled) return;
  isVisible.value = !isVisible.value;
};
</script>

<template>
  <BaseInput
    :model-value="modelValue"
    :type="inputType"
    :placeholder="placeholder"
    :disabled="disabled"
    :autocomplete="autocomplete"
    :error="error"
    :error-message="unref(errorMessage)"
    :name="name"
    @update:model-value="emit('update:modelValue', $event)"
    @blur="emit('blur')"
    @focus="emit('focus')"
  >
    <template #suffix>
      <button
        type="button"
        class="flex size-24 items-center justify-center text-off-black-500 transition-opacity hover:opacity-70"
        :aria-label="toggleLabel"
        :disabled="disabled"
        @click="toggleVisibility"
      >
        <svg
          v-if="isVisible"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          class="size-24"
        >
          <path
            d="M3 4L20 21"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
          <path
            d="M10.584 10.587C10.22 10.95 10 11.45 10 12C10 13.1 10.9 14 12 14C12.55 14 13.05 13.78 13.413 13.416"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M9.88 5.09C10.57 4.86 11.28 4.75 12 4.75C16.78 4.75 20.98 8.89 22 12C21.53 13.43 20.54 15.13 19.12 16.52"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M6.53 7.53C4.52 8.88 3.2 10.72 2 12C2.47 13.43 3.46 15.13 4.88 16.52C6.41 18.02 8.56 19.25 12 19.25C13.41 19.25 14.65 19.04 15.75 18.66"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          class="size-24"
        >
          <path
            d="M12 5C7 5 3.73 8.11 2 12C3.73 15.89 7 19 12 19C17 19 20.27 15.89 22 12C20.27 8.11 17 5 12 5Z"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <circle
            cx="12"
            cy="12"
            r="3"
            stroke="currentColor"
            stroke-width="2"
          />
        </svg>
      </button>
    </template>
  </BaseInput>
</template>
